/**
 * WorkItemsPage — operator surface for the in-platform Work Item backlog
 * (WQ-298 / ADR-019 D2). The single place a human sees + acts on the REM-
 * remediation items the orchestrator receiver mints (WQ-295/297). Mirrors the
 * Security Dashboard's ThreatsTab (filter toolbar + table + pagination +
 * usePollingResource) with a deep-linked side-panel detail (?work_item_id=).
 *
 * Filed under the Operations nav group — the registry is the platform-wide
 * backlog for both ADR lanes (today only security REMs, the one wired producer).
 */
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  EmptyState,
  ErrorState,
  LoadingSkeleton,
} from '../features/security/components/LoadingSkeleton';
import { useWorkItems } from '../features/work-items/hooks/useWorkItems';
import { WorkItemDetailPanel } from '../features/work-items/components/WorkItemDetailPanel';
import {
  WORK_ITEM_LANES,
  WORK_ITEM_SEVERITIES,
  WORK_ITEM_STATUSES,
  type WorkItemFilters,
  type WorkItemLane,
  type WorkItemSeverity,
  type WorkItemStatus,
} from '../features/work-items/types/workItems';

const LIMIT = 50;

const WorkItemsPage: React.FC = () => {
  const [, setSearchParams] = useSearchParams();
  const [lane, setLane] = useState<WorkItemLane | ''>('');
  const [status, setStatus] = useState<WorkItemStatus | ''>('');
  const [severity, setSeverity] = useState<WorkItemSeverity | ''>('');
  const [skill, setSkill] = useState('');
  const [offset, setOffset] = useState(0);

  const filters: WorkItemFilters = useMemo(
    () => ({
      lane: lane || undefined,
      status: status || undefined,
      severity_token: severity || undefined,
      originating_skill: skill.trim() || undefined,
      limit: LIMIT,
      offset,
    }),
    [lane, status, severity, skill, offset],
  );

  const { data, isLoading, error, refetch } = useWorkItems(filters);
  const items = data?.items ?? [];

  const openDetail = (id: string) => {
    setSearchParams((prev) => {
      prev.set('work_item_id', id);
      return prev;
    });
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <header style={{ marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Work Items</h2>
        <p style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', fontSize: '0.875rem' }}>
          The in-platform remediation backlog. REM- items are proposed by the
          orchestrator receiver (review-only) — a human triages them here.
        </p>
      </header>

      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginBottom: '1rem',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem' }}>Lane</span>
          <select
            value={lane}
            onChange={(e) => { setLane(e.target.value as WorkItemLane | ''); setOffset(0); }}
            aria-label="Filter by lane"
            style={{ padding: '0.25rem 0.5rem' }}
          >
            <option value="">All lanes</option>
            {WORK_ITEM_LANES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem' }}>Status</span>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value as WorkItemStatus | ''); setOffset(0); }}
            aria-label="Filter by status"
            style={{ padding: '0.25rem 0.5rem' }}
          >
            <option value="">All statuses</option>
            {WORK_ITEM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem' }}>Severity</span>
          <select
            value={severity}
            onChange={(e) => { setSeverity(e.target.value as WorkItemSeverity | ''); setOffset(0); }}
            aria-label="Filter by severity"
            style={{ padding: '0.25rem 0.5rem' }}
          >
            <option value="">All severities</option>
            {WORK_ITEM_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem' }}>Origin skill</span>
          <input
            type="search"
            value={skill}
            onChange={(e) => { setSkill(e.target.value); setOffset(0); }}
            placeholder="cyber-agent"
            aria-label="Filter by originating skill"
            style={{ padding: '0.25rem 0.5rem' }}
          />
        </label>
      </div>

      {isLoading && !data && <LoadingSkeleton />}
      {error && <ErrorState error={error} onRetry={refetch} />}
      {!isLoading && data && items.length === 0 && (
        <EmptyState
          message={
            'No work items match current filters. REM- items appear here when an ' +
            'operator sends a Security Dashboard event to the orchestrator.'
          }
        />
      )}

      {data && items.length > 0 && (
        <>
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}
            aria-label="Work items"
          >
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ padding: '0.5rem' }}>ID</th>
                <th style={{ padding: '0.5rem' }}>Title</th>
                <th style={{ padding: '0.5rem' }}>Lane</th>
                <th style={{ padding: '0.5rem' }}>Status</th>
                <th style={{ padding: '0.5rem' }}>Severity</th>
                <th style={{ padding: '0.5rem' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openDetail(item.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openDetail(item.id);
                    }
                  }}
                  style={{ cursor: 'pointer', borderTop: '1px solid var(--border-default, #e5e7eb)' }}
                >
                  <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>
                    {item.display_id ?? <em style={{ color: 'var(--text-secondary)' }}>—</em>}
                  </td>
                  <td style={{ padding: '0.5rem' }}>{item.title}</td>
                  <td style={{ padding: '0.5rem', fontSize: '0.75rem' }}>{item.lane}</td>
                  <td style={{ padding: '0.5rem' }}>{item.status}</td>
                  <td style={{ padding: '0.5rem' }}>{item.severity_token}</td>
                  <td style={{ padding: '0.5rem', fontSize: '0.75rem' }}>
                    {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '0.75rem',
              fontSize: '0.875rem',
            }}
          >
            <div>Showing {items.length} of {data.total} work items</div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                disabled={offset === 0}
                className="btn btn-secondary"
              >
                Previous
              </button>
              <button
                onClick={() => setOffset(offset + LIMIT)}
                disabled={items.length < LIMIT || offset + LIMIT >= data.total}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      <WorkItemDetailPanel onChanged={refetch} />
    </div>
  );
};

export default WorkItemsPage;
